import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { useHistory } from "react-router";
import styled from "@emotion/styled";
import css from "@emotion/css";
import Controller from "./Controller";
import ControllerLegacy from "./Controller/ControllerLegacy";
import { isScoreBeta } from "../../utils/beta";
import CountOverlay from "./CountOverlay";
import NoteBar, { INoteBar } from "./NoteBar";
import playCounter, { delayForMilliseconds } from "./playCounter";
import { INoteData, IMusic } from "./types";

import useInterval from "../../utils/useInterval";
import useWindowSize from "../../utils/useWindowSize";
import useHiddenBodyOverflow from "../../utils/useHiddenBodyOverflow";
import speedState from "../../recoil/speedState";

import Modal from "../Modal";
import { TextArea } from "../Input";
import { api } from "../../utils/api";
import notesPerLineState from "../../recoil/notesPerLineState";
import chordSizeState from "../../recoil/chordSizeState";

import ReportIcon from "../../assets/icons/report.svg";
import FullScreenIcon from '../../assets/full_screen.png';
import ExitFullScreenIcon from '../../assets/exit_full_screen.png';
import RotateIcon from '../../assets/rotate_icon.png';
import DownloadIcon from '../../assets/file-text.svg';
import ShareIcon from '../../assets/share.png';
import isReportModalOpenState from "../../recoil/isReportModalOpenState";
import auth from "../../utils/auth";
import Hammer from "hammerjs";
import html2canvas from "html2canvas";
import jspdf from "jspdf";
import useQueryString from "../../utils/useQueryString";
import LyricsView from "./LyricsView";
import CapoSheet from "./CapoSheet";
import simpleChordState from "../../recoil/simpleChordState";
import { simplifyChord } from "./simpleChord";
import capoState from "../../recoil/capoState";
import baseKeyState from "../../recoil/baseKeyState";
import { transposeChord } from "./NoteBar/Chord";
import { AccStyle, playBeat, prepareAccompany } from "./accompany";
import VideoSync from "./VideoSync";
import { quarterBeats, readMeter } from "./meter";
import { click as metroClick, prepareMetronome } from "./metronome";
import PracticeCheck from "./PracticeCheck";
import { startListening, stopListening } from "./practice";
import { track } from "../../utils/track";
import { noPurchaseUI } from "../../utils/billing";
import { useEnsemble, roomLink } from "./ensemble";
import { isEn } from "../../utils/i18n";
import EnsembleBar from "./EnsembleBar";
const metronomeAudio = require('../../assets/audio/metronome.wav');

export interface INotePlayer {
  data: INoteData;
  handleFullScreen?: (isFullScreen: boolean) => void;
}

const NotePlayer: React.FC<INotePlayer> = ({ data: { info, musics, preview: previewData, free: isFreeSong, personal }, handleFullScreen }) => {
  const history = useHistory();
  useHiddenBodyOverflow();
  const [print] = useState(false);
  const [isControllerHidden, setIsControllerHidden] = useState<boolean>(false);
  const [isReportModalOpen, setIsReportModalOpen] = useRecoilState<boolean>(
    isReportModalOpenState
  );
  const [isFullScreen, setIsFullScreen] = useState<boolean>(false);
  // 새 악보 화면은 ?beta=1 로 켠 기기에서만 (일반 회원은 기존 화면)
  const [beta] = useState<boolean>(() => isScoreBeta());
  // 악보 보기 방식: 'bars' = 마디 보기(기존), 'lyrics' = 가사 위에 코드만. 마지막 선택을 기억한다.
  const [viewMode, setViewModeState] = useState<"bars" | "lyrics">(() => {
    try {
      // 글로벌(영어) 화면은 가사 보기만 (2026-10-11 사장님: 마디 악보는 영어 표기가 어려워 뺌)
      if (isScoreBeta() && isEn()) return "lyrics";
      return isScoreBeta() && localStorage.getItem("scoreViewMode") === "lyrics" ? "lyrics" : "bars";
    } catch (e) {
      return "bars";
    }
  });
  const setViewMode = (m: "bars" | "lyrics") => {
    track(m === "bars" ? "score:view_bars" : "score:view_lyrics");
    setViewModeState(m);
    try {
      localStorage.setItem("scoreViewMode", m);
    } catch (e) {}
  };
  // 보기 방식 이용 기록 (2026-10-05 사장님: 가사 보기와 마디 보기 중 어느 쪽을 많이 보는지)
  // 열 때 보기(score:open_bars / open_lyrics) + 보기별로 머문 시간(score:tm:bars:초, 15초 단위, 최대 30분)
  const openedView = useRef(false);
  useEffect(() => {
    if (!beta) return;
    if (!openedView.current) {
      openedView.current = true;
      track(viewMode === "bars" ? "score:open_bars" : "score:open_lyrics");
    }
    let start = Date.now();
    const flush = () => {
      const sec = Math.min(1800, Math.round((Date.now() - start) / 1000 / 15) * 15);
      if (sec >= 15) track(`score:tm:${viewMode}:${sec}`);
      start = Date.now();
    };
    // 화면을 끄거나 다른 앱으로 간 시간은 빼기
    const onVis = () => (document.visibilityState === "hidden" ? flush() : (start = Date.now()));
    document.addEventListener("visibilitychange", onVis);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      if (document.visibilityState !== "hidden") flush();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);
  const [isCapoOpen, setIsCapoOpen] = useState<boolean>(false);
  // 이 곡의 유튜브 기타 강좌 영상 (있으면 도구줄에 '강좌' 버튼)
  const [tutorial, setTutorial] = useState<{ id: string; title: string; channel: string } | null>(null);
  // 강좌 영상은 유튜브로 나가지 않고 악보 위 창에서 재생 (2026-10-07 사장님)
  const [showTutorial, setShowTutorial] = useState(false);
  // 원곡 영상과 같이 흘러가는 악보: 원곡 비교로 공식 영상·시작 위치를 찾은 곡만
  const [video, setVideo] = useState<{ id: string; offset: number; shift?: number } | null>(null);
  const [showVideo, setShowVideo] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  // 연주 체크: 마이크로 듣고 지금 코드를 맞게 치는지 알려 줌
  const [practiceOn, setPracticeOn] = useState(false);
  const [practiceErr, setPracticeErr] = useState("");
  const togglePractice = async () => {
    if (practiceOn) {
      stopListening();
      setPracticeOn(false);
      return;
    }
    track("score:practice");
    setPracticeErr("");
    setPracticeOn(true);
    try {
      await startListening();
    } catch (e) {
      setPracticeErr("마이크를 쓸 수 없어요. 앱을 최신으로 업데이트하고 마이크를 허용해 주세요");
    }
  };
  useEffect(() => () => stopListening(), []);
  useEffect(() => {
    if (!beta || !info?.id || personal) return;
    api.get("/tutorial", { params: { id: info.id } }).then(({ data }) => setTutorial(data?.data || null)).catch(() => {});
    api.get("/video", { params: { id: info.id } }).then(({ data }) => setVideo(data?.data || null)).catch(() => {});
  }, [beta, info?.id]);
  const capo = useRecoilValue(capoState);
  const [baseKeyForAcc, setBaseKeyForOrig] = useRecoilState(baseKeyState);
  const [capoForOrig, setCapoForOrig] = useRecoilState(capoState);
  // 원곡 키: 원곡 비교로 '악보를 반음 몇 개 옮기면 원곡과 같은지' 확실한 곡만 (video.shift, 0이면 같은 키)
  const origShift = video && video.shift ? (video.shift > 6 ? video.shift - 12 : video.shift) : 0;
  const atOrigKey = origShift !== 0 && !capoForOrig && ((baseKeyForAcc - origShift) % 12 + 12) % 12 === 0;
  const toOrigKey = () => {
    track("score:origkey");
    setCapoForOrig(null);
    setBaseKeyForOrig(origShift);
  };
  const simpleChord = useRecoilValue(simpleChordState);
  const soundChord = (c: string) => {
    const t = transposeChord(c, ((baseKeyForAcc % 12) + 12) % 12);
    return simpleChord ? simplifyChord(t) : t;
  };
  // 반주(기타 소리): 끔 → 스트로크 → 아르페지오. 마지막 선택을 기억
  const [accStyle, setAccStyle] = useState<AccStyle>(() => {
    try {
      const v = localStorage.getItem("accStyle");
      return v === "strum" || v === "arpeggio" ? v : "off";
    } catch (e) {
      return "off";
    }
  });
  const cycleAcc = async () => {
    const next: AccStyle = accStyle === "off" ? "strum" : accStyle === "strum" ? "arpeggio" : "off";
    if (next !== "off") {
      track("score:accompany");
      await prepareAccompany(); // 버튼을 누른 순간에 소리 준비 (휴대폰은 터치 후에만 소리 허용)
    }
    setAccStyle(next);
    try {
      localStorage.setItem("accStyle", next);
    } catch (e) {}
  };
  const [enableMetronome, setEnableMetronome] = useState<boolean>(false);
  const [reportValue, setReportValue] = useState<string>("");

  const noteContainerRef = useRef<HTMLDivElement>(null);
  const noteScrollContainerRef = useRef<HTMLDivElement>(null);
  const [noteBarHeight, setNoteBarHeight] = useState<number>(0);
  const [noteBarList, setNoteBarList] = useState<INoteBar[]>([]);

  // TODO: set isPlaying to false later on
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentProgress, setCurrentProgress] = useState<number>(0);
  const [speed, setSpeedForRoom] = useRecoilState(speedState);
  const [isVisibleSpeed, setIsVisibleSpeed] = useState<boolean>(false);
  const [lyricsWidth, setLyricsWidth] = useState<number>(0);
  const [isCopyModelOpen, setIsCopyModelOpen] = useState<boolean>(false);
  const [allChordCode, setAllChordCode] = useState<any[]>([]);

  const [notesPerLine, setNotesPerLine] = useRecoilState(notesPerLineState);
  const chordSize = useRecoilValue(chordSizeState);
  const audio = new Audio(metronomeAudio);
  const query = useQueryString();
  audio.load();

  const {
    height: screenHeight = 1080,
    width: screenWidth = 1980,
  } = useWindowSize();

  useEffect(() => {
    setTimeout(() => {
      try {
        const firstChordCodeContainer = document.getElementsByClassName(
          "chord-code-container"
        )[0] as HTMLDivElement;
        setLyricsWidth(firstChordCodeContainer.offsetWidth * 2);
      } catch (error) {
        console.log(error);
      }
    }, 500);
  }, [screenWidth, screenHeight, chordSize]);

  useEffect(() => {
    if (!musics) {
      return;
    }
    setNoteBarList(
      musics.flatMap(
        ({
          chord_1,
          chord_2,
          chord_3,
          chord_4,
          chord_5,
          chord_6,
          chord_7,
          chord_8,
          chord_9,
          chord_10,
          chord_11,
          chord_12,
          chord_13,
          chord_14,
          chord_15,
          chord_16,
          lyrics_1,
          lyrics_2,
          lyrics_3,
          lyrics_4,
        }: IMusic) => [
            {
              chords: [chord_1, chord_2, chord_3, chord_4],
              lyrics: lyrics_1,
            },
            {
              chords: [chord_5, chord_6, chord_7, chord_8],
              lyrics: lyrics_2,
            },
            {
              chords: [chord_9, chord_10, chord_11, chord_12],
              lyrics: lyrics_3,
            },
            {
              chords: [chord_13, chord_14, chord_15, chord_16],
              lyrics: lyrics_4,
            },
          ]
      ).map((bar: INoteBar) => {
        // 가사 칸에 적어 둔 '(2/4박자)' 같은 박자 바뀜 표시 → 마디 길이·표시로 (가사에서는 지움)
        const m = readMeter(bar.lyrics, quarterBeats(info?.beat));
        return { ...bar, lyrics: m.lyrics, meter: m.meter, scale: m.scale };
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [musics, info?.beat]);

  // 마디마다 길이 배율 (박자가 바뀐 마디만 1이 아님)
  const barScale = (bar: number) => noteBarList[bar]?.scale || 1;

  const getSizeWithBreakpoint = useCallback(
    ([mobileSize, tabletSize, defaultSize]: number[]) =>
      screenWidth <= 1200
        ? screenWidth <= 768
          ? mobileSize
          : tabletSize
        : defaultSize,
    [screenWidth]
  );

  const noteContainerTopPadding = getSizeWithBreakpoint([25, 40, 50]);
  const noteContainerBottomPadding = getSizeWithBreakpoint([95, 100, 50]);
  const noteContainerPadding =
    noteContainerTopPadding + noteContainerBottomPadding;
  const headerHeight = getSizeWithBreakpoint([12 + 28, 32 + 98, 38 + 108]);

  const totalLines = Math.ceil(noteBarList.length / notesPerLine);

  useEffect(() => {
    function handleResize() {
      if (noteContainerRef && noteContainerRef.current) {
        const noteContainerHeight = parseInt(
          window.getComputedStyle(noteContainerRef.current).height
        );

        const noteListWrapperHeight =
          noteContainerHeight - noteContainerPadding - headerHeight;
        setNoteBarHeight(noteListWrapperHeight / totalLines);
      }
    }

    // 가사 보기에서 마디 보기로 바꾸면 줄 높이를 다시 재야 재생 위치로 제대로 따라감 (2026-10-04 신고)
    const t = setTimeout(handleResize, viewMode === "bars" ? 80 : 500);
    const t2 = setTimeout(handleResize, 600);

    window.addEventListener("resize", handleResize);
    return () => {
      clearTimeout(t);
      clearTimeout(t2);
      window.removeEventListener("resize", handleResize);
    };
  }, [
    viewMode,
    musics.length,
    totalLines,
    headerHeight,
    noteContainerPadding,
    getSizeWithBreakpoint,
  ]);

  useEffect(() => {
    if (isFullScreen) {
      var header = document.getElementById("header") as HTMLElement;
      header.style.display = 'none';
      if (window.chordscore) {
        window.chordscore.postMessage('hideNavigationBar');
      }
      // document.getElementById('note-wrap')?.addEventListener('touchstart', stopFullScreen);
    } else {
      var header = document.getElementById("header") as HTMLElement;
      header.style.display = 'flex';
      if (window.chordscore) {
        window.chordscore.postMessage('showNavigationBar');
      }
    }
  }, [isFullScreen])

  var varUA = navigator.userAgent;
  // alert(window.webkit.messageHandlers)
  const handleRotate = () => {
    if (varUA.match('chordscore/Android') != null) {
      window.chordscore.postMessage('rotateScreen');
    } else if (varUA.match('chordscore/iOS') != null) {
      window.webkit.messageHandlers.rotateScreenHandler.postMessage('_');
    }
  }

  useEffect(() => {
    if (varUA.match('chordscore/Android') != null) {
      window.chordscore.postMessage(JSON.stringify({ type: 'playAndPause', isPlaying, enableMetronome: enableMetronome && !beta, speed, interval: intervalMetro }));
    } else if (varUA.match('chordscore/iOS') != null && window.webkit.messageHandlers.playAndPauseHandler) {
      window.webkit.messageHandlers.playAndPauseHandler.postMessage({ type: 'playAndPause', isPlaying, enableMetronome: enableMetronome && !beta, speed, interval: intervalMetro });
    }
  }, [isPlaying, speed, enableMetronome])

  useEffect(() => {
    const nav: any = navigator;
    if (!beta || !(isFullScreen || isPlaying) || !nav.wakeLock) return;
    let lock: any = null;
    let released = false;
    const request = () =>
      nav.wakeLock
        .request("screen")
        .then((l: any) => {
          if (released) l.release();
          else lock = l;
        })
        .catch(() => {});
    request();
    const onVisible = () => {
      if (document.visibilityState === "visible" && !released) request();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      released = true;
      document.removeEventListener("visibilitychange", onVisible);
      if (lock) lock.release().catch(() => {});
    };
  }, [isFullScreen, isPlaying]);

  const resetPlay = () => {
    if (varUA.match('chordscore/Android') != null) {
      window.chordscore.postMessage(JSON.stringify({ type: 'playAndPause', isPlaying: false, enableMetronome: false, speed, interval: intervalMetro }));
    } else if (varUA.match('chordscore/iOS') != null && window.webkit.messageHandlers.playAndPauseHandler) {
      window.webkit.messageHandlers.playAndPauseHandler.postMessage(JSON.stringify({ type: 'playAndPause', isPlaying: false, enableMetronome: false, speed, interval: intervalMetro }));
    }
  }

  const handleShare = () => {
    if (varUA.match('chordscore/Android') != null) {
      window.chordscore.postMessage(JSON.stringify({ type: 'share', text: window.location.href }));
    } else if (varUA.match('chordscore/iOS') != null) {
      window.webkit.messageHandlers.shareHandler.postMessage(window.location.href);
    } else {
      const elem = document.createElement('textarea');
      elem.value = window.location.href;
      document.body.appendChild(elem);
      elem.select();
      document.execCommand('copy');
      document.body.removeChild(elem);
      setIsCopyModelOpen(true);
    }
  }

  const resetRotate = () => {
    if (varUA.match('chordscore/Android') != null) {
      window.chordscore.postMessage('resetRotateScreen');
    } else if (varUA.match('chordscore/iOS') != null) {
      window.webkit.messageHandlers.rotateScreenHandler.postMessage('reset');
    }
  }

  const onClickToggleController = () =>
    setIsControllerHidden(!isControllerHidden);

  const enterPlayMode = () => {
    track("score:play_mode");
    handleFullScreen && handleFullScreen(true);
    setIsFullScreen(true);
    setIsControllerHidden(true);
  };
  const exitPlayMode = () => {
    handleFullScreen && handleFullScreen(false);
    setIsFullScreen(false);
    setIsControllerHidden(false);
  };

  const totalDurationOffset = musics.length * 400;

  const calculateStepProgress = () => {
    const beat = info?.beat.split('/');
    if (beat?.length != 2) return 25;

    const firstBeat = Number(beat[0]) >= Number(beat[1]) ? Number(beat[1]) : Number(beat[0]);
    const secondBeat = Number(beat[0]) >= Number(beat[1]) ? Number(beat[0]) : Number(beat[1]);
    if (firstBeat == 0 || secondBeat == 0) return 25;

    switch (`${firstBeat}/${secondBeat}`) {
      case '2/4':
        return 50;
      case '3/4':
        return 100 / 3;
      case '4/4':
        return 25;
      case '6/8':
        return 100 / 6;
      default:
        return 25;
    }
  }

  const interval = useMemo(() => {
    if (info?.sec) {
      return (Number(info?.sec) * 1000) / 4 / speed;
    }
    const [minutes, seconds] = info?.time.split(":");
    const totalDurationTime = parseInt(minutes) * 60 + parseInt(seconds);
    return ((totalDurationTime / noteBarList.length) * 25 * 10) / speed;
  }, [info, speed, noteBarList]);

  const intervalMetro = useMemo(() => {
    const step = calculateStepProgress();
    if (info?.sec) {
      return (Number(info?.sec) * 1000) / (100 / step) / speed;
    }
    const [minutes, seconds] = info?.time.split(":");
    const totalDurationTime = parseInt(minutes) * 60 + parseInt(seconds);
    return ((totalDurationTime / noteBarList.length) * step * 10) / speed;
  }, [info, speed, noteBarList]);

  useInterval(() => {
    if (isPlaying && !videoPlaying) {
      if (currentProgress > totalDurationOffset) {
        // init scroll offset
        // setCurrentProgress(0);
        setIsPlaying(false);

        // if (noteScrollContainerRef && noteScrollContainerRef.current) {
        //   noteScrollContainerRef.current.scrollTop = 0;
        // }
        return;
      }

      const step = calculateStepProgress();
      setCurrentProgress(currentProgress + 25);

      // if (enableMetronome) {
      //   audio.play();
      // }
    }
  }, interval * barScale(Math.floor(currentProgress / 100)));

  useInterval(() => {
    if (isPlaying) {
      var varUA = navigator.userAgent;
      if (enableMetronome && !beta) {
        if (varUA.match('chordscore/iOS') != null) {
          window.webkit.messageHandlers.playMetroHandler.postMessage('_');
        } else if (varUA.match('chordscore/Android') != null) {
          audio.play();
          window.chordscore.postMessage('playMetro');
        }
        else {
          audio.play();
        }
      }
    }
  }, intervalMetro);

  const [countStep, setCountStep] = useState(0);
  const [countValue, setCountValue] = useState("3");
  const [isCounterRendered, setIsCounterRendered] = useState(false);
  const [isTransitionDisabled, setIsTransitionDisabled] = useState(false);

  const onClickPlayButton = async () => {
    if (!isPlaying) track("score:play");
    // 반주가 켜진 채로 페이지를 다시 열었으면 소리가 아직 준비 안 됨 → 재생을 누른 순간 준비 (2026-10-04 신고)
    if (!isPlaying && accStyle !== "off") prepareAccompany();
    if (isPlaying) {
      setIsPlaying(false);
      return;
    }

    if (currentProgress > totalDurationOffset) { // Case end of music
      // init scroll offset
      scollToTop();
      setIsTransitionDisabled(true);
      setTimeout(() => setIsTransitionDisabled(false), speedInSeconds);
      setCurrentProgress(0);
      setIsCounterRendered(true);
      await playCounter({
        setCountStep,
        setCountValue,
      });
      setIsCounterRendered(false);
      setCountStep(0);
      setCountValue("3");
      setIsPlaying(true);
      delayForMilliseconds(300);
      return;
    }

    if (currentProgress === 0) {
      setIsCounterRendered(true);
      await playCounter({
        setCountStep,
        setCountValue,
      });
      setIsCounterRendered(false);
      setCountStep(0);
      setCountValue("3");
      setIsPlaying(true);
      delayForMilliseconds(300);
      return;
    }
    setIsPlaying(true);
  };

  const handleReport = async () => {
    api.post("/score/report", {
      score_id: info?.id,
      score_contents: reportValue,
    });
    setIsReportModalOpen(false);
  };

  const currentLine = useMemo(
    () => Math.floor(currentProgress / (100 * notesPerLine)),
    [currentProgress, notesPerLine]
  );

  const speedInSeconds = useMemo(() => interval / 1000, [interval]);

  // 자동 스크롤이 한 칸(박) 나아갈 때마다 그 박의 코드를 기타로 친다
  useEffect(() => {
    if (!beta || !isPlaying || accStyle === "off" || currentProgress % 25 !== 0) return;
    const bar = Math.floor(currentProgress / 100);
    const step = Math.floor((currentProgress % 100) / 25);
    if (bar >= noteBarList.length) return;
    let chord = "";
    for (let b = bar, s = step; b >= 0 && !chord; b--, s = 3) {
      for (let k = s; k >= 0; k--) {
        const c = (noteBarList[b]?.chords[k] || "").trim();
        if (c) {
          chord = c;
          break;
        }
      }
    }
    if (!chord) return;
    const beatsPerBar = Number((info?.beat || "4/4").split("/")[0]) === 3 ? 3 : 4;
    playBeat(soundChord(chord), step, beatsPerBar, (interval / 1000) * barScale(bar), accStyle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProgress, isPlaying]);

  // 메트로놈: 악보가 한 칸 나아갈 때마다 클릭, 마디 첫 박은 강세
  useEffect(() => {
    if (!beta || !enableMetronome || !(isPlaying || videoPlaying) || currentProgress % 25 !== 0) return;
    metroClick(currentProgress % 100 === 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProgress, isPlaying]);

  // 연주 체크용: 지금 칸에서 울려야 하는 코드(키 바꾼 소리 기준)와 곡에 나오는 코드들
  const practiceBar = Math.min(Math.floor(currentProgress / 100), Math.max(0, noteBarList.length - 1));
  const practiceChord = useMemo(() => {
    if (!practiceOn) return "";
    const step = Math.floor((currentProgress % 100) / 25);
    for (let b = practiceBar, s = step; b >= 0; b--, s = 3) {
      for (let k = s; k >= 0; k--) {
        const c = (noteBarList[b]?.chords[k] || "").trim();
        if (c) return soundChord(c);
      }
    }
    return "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceOn, currentProgress, practiceBar, noteBarList, baseKeyForAcc, simpleChord]);
  const songChords = useMemo(() => {
    if (!practiceOn) return [] as string[];
    const all: string[] = [];
    noteBarList.forEach((nb: any) => (nb?.chords || []).forEach((c: string) => c && c.trim() && all.push(c.trim())));
    return Array.from(new Set(all.map(soundChord)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceOn, noteBarList, baseKeyForAcc, simpleChord]);

  // 원곡 빠르기(BPM): 한 마디 길이와 박자로 계산
  const songBpm = useMemo(() => {
    if (!interval || !speed) return 0;
    const num = Number((info?.beat || "4/4").split("/")[0]);
    const beats = num === 3 ? 3 : num === 6 ? 2 : 4;
    return (beats * 60000) / (4 * interval * speed);
  }, [interval, speed, info]);

  // 곡 전체 길이(초, 1배속 기준): sec = 마디당 초. 없으면 time "분:초"
  const totalDurationSec = useMemo(() => {
    if (info?.sec) return Number(info.sec) * noteBarList.reduce((a, b) => a + (b.scale || 1), 0);
    const [m, sec] = (info?.time || "0:0").split(":");
    return (parseInt(m) || 0) * 60 + (parseInt(sec) || 0);
  }, [info, noteBarList]);

  useEffect(() => {
    if (!(isPlaying || videoPlaying) || viewMode === "lyrics") {
      return;
    }

    const scrollInterval = setInterval(() => {
      const box = noteScrollContainerRef && noteScrollContainerRef.current;
      if (beta && box) {
        // 새 디자인: 지금 줄의 실제 위치를 재서 화면 가운데쯤(위에서 40%)에 오게 (보기 전환·글자 크기 변경에도 정확)
        const el = box.querySelector(`[data-nb="${currentLine * notesPerLine}"]`) as HTMLElement | null;
        if (el) {
          const top = el.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
          box.scrollTop = Math.max(0, top - box.clientHeight * 0.4 + el.offsetHeight / 2);
          return;
        }
      }
      if (noteScrollContainerRef && noteScrollContainerRef.current) {
        const extraSpace = screenWidth <= 768 ? 70 : 0;

        noteScrollContainerRef.current.scrollTop =
          noteBarHeight *
          (screenHeight <= 500
            ? currentLine + (1.5 - 0.06 * currentLine)
            : currentLine) -
          extraSpace;
      }
    }, interval);

    return () => clearInterval(scrollInterval);
  }, [
    isPlaying,
    interval,
    currentLine,
    notesPerLine,
    noteBarHeight,
    headerHeight,
    noteContainerTopPadding,
    screenHeight,
    viewMode,
    videoPlaying,
  ]);

  // change line functions

  // useEffect(() => {
  //   console.log({ minusLine, plusLine });
  //   if (minusLine) {
  //     if (notesPerLine > 1) {
  //       console.log('minus');
  //       setNotesPerLine((prevState) => prevState - 1);
  //       setMinusLine(false);
  //     }
  //   }
  //   // } else if (!minusLine && plusLine) {
  //   //   if (notesPerLine < 4) {
  //   //     console.log('plus');
  //   //     setNotesPerLine((prevState) => prevState + 1);
  //   //     setPlusLine(false);
  //   //   }
  //   // }
  // }, [minusLine, plusLine]);

  useEffect(() => {
    if (chordSize >= 2.5) {
      setNotesPerLine(1);
    } else if (chordSize >= 2) {
      setNotesPerLine(2);
    } else if (chordSize >= 1.5) {
      setNotesPerLine(3);
    } else if (chordSize === 1) {
      setNotesPerLine(4);
    }
  }, [chordSize, setNotesPerLine]);

  const refNotesPerLine = useRef<number>(4);

  useEffect(() => {
    refNotesPerLine.current = notesPerLine;
  }, [notesPerLine]);

  const handlePinAndPan = () => {
    const el = document.getElementById('note-wrap') as HTMLElement;
    var hammer = new Hammer(el, {
      domEvents: true,
    });

    hammer.get('pinch').set({
      enable: true,
    });
    el.style.touchAction = 'unset';
    let countPinchin = 0;
    let countPinchout = 0;

    hammer.on('pinchin', (e) => {
      countPinchin += 1;
    })

    hammer.on('pinchout', (e) => {
      countPinchout += 1;
    })

    hammer.on('pinchcancel', (e) => {
      console.log(countPinchin > countPinchout);
      if (countPinchin > countPinchout) {
        if (refNotesPerLine.current > 1) {
          setNotesPerLine(refNotesPerLine.current - 1)
        }
      }
      if (countPinchin < countPinchout) {
        if (refNotesPerLine.current < 4) {
          setNotesPerLine(refNotesPerLine.current + 1)
        }
      }
      countPinchin = 0;
      countPinchout = 0;
    })
  }

  useEffect(() => {
    document.title = (info ? info.title : '') + ' 코드악보_코드스코어';
    if (auth.getToken()) {
      tryFetchSetting();
    }

    getAllChordCode();

    // handlePinAndPan();
    return () => {
      resetRotate();
      resetPlay();
      document.title = '코드스코어, 기타코드악보 열람';
    }
  }, []);

  const getAllChordCode = async () => {
    const {
      data: { data },
    } = await api.get(`/chords`);
    setAllChordCode(data);
  }

  const tryFetchSetting = async () => {
    const { data: { valid } } = await api.get("/check_valid_token");
    if (!valid) return;

    const {
      data: { data },
    } = await api.get("/setting");
    setIsVisibleSpeed(data.view_speed ? false : true);
  }

  const onClickNoteBar = (threshold?: number) => {
    if (localStorage.getItem('isDisableClickChordLine') == 'true') {
      localStorage.removeItem('isDisableClickChordLine');
      return;
    }

    if (threshold) {
      setCurrentProgress(threshold);
    }
    setIsTransitionDisabled(true);
    setTimeout(() => setIsTransitionDisabled(false), speedInSeconds);
  };

  const onClickReset = async () => {
    if (accStyle !== "off") prepareAccompany();
    setIsPlaying(false);
    setIsTransitionDisabled(true);
    setCurrentProgress(0);
    setTimeout(() => setIsTransitionDisabled(false), speedInSeconds);
    scollToTop();

    setIsCounterRendered(true);
    await playCounter({
      setCountStep,
      setCountValue,
    });
    setIsCounterRendered(false);
    setCountStep(0);
    setCountValue("3");
    setIsPlaying(true);
    delayForMilliseconds(300);
  }

  const scollToTop = () => {
    if (noteScrollContainerRef && noteScrollContainerRef.current) {
      noteScrollContainerRef.current.scrollTop = 0;
    }
  }

  const downloadPDF = async () => {
    if (noteBarList.length === 0) return;
    const el = document.querySelector('#note-container') as HTMLElement;
    const canvas = await html2canvas(el);
    const imageFile = canvas.toDataURL('image/png');
    const doc = new jspdf('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const widthRatio = pageWidth / canvas.width;
    const customHeight = canvas.height * widthRatio;

    doc.addImage(imageFile, 'png', 0, 0, pageWidth, customHeight);

    let heightLeft = customHeight;
    let heightAdd = -pageHeight;

    while (heightLeft >= pageHeight) {
      doc.addPage();
      doc.addImage(imageFile, 'png', 0, heightAdd, pageWidth, customHeight);
      heightLeft -= pageHeight;
      heightAdd -= pageHeight;
    }

    doc.save('filename' + new Date().getTime() + '.pdf');
  }

  useEffect(() => {
    if (query.downloadPDF) {
      downloadPDF();
    }
  }, [noteBarList]);

  // 합주방: 방장 화면(재생·위치·속도·키)을 같이 (ensemble.ts)
  const room = useEnsemble({
    musicId: personal ? 0 : Number(info?.id) || 0,
    enabled: beta && !personal,
    local: { playing: isPlaying, progress: currentProgress, speed, key: baseKeyForAcc, capo: capoForOrig },
    interval: interval * barScale(Math.floor(currentProgress / 100)),
    apply: (p) => {
      if (p.speed !== undefined) setSpeedForRoom(p.speed);
      if (p.key !== undefined) setBaseKeyForOrig(p.key);
      if (p.capo !== undefined) setCapoForOrig(p.capo);
      if (p.progress !== undefined) {
        setIsTransitionDisabled(true);
        setCurrentProgress(p.progress);
        setTimeout(() => setIsTransitionDisabled(false), speedInSeconds);
      }
      if (p.playing !== undefined) setIsPlaying(p.playing);
    },
  });

  return (
    <>
      {showTutorial && tutorial && (
        <TutorialDim onClick={() => setShowTutorial(false)}>
          <TutorialBox onClick={(e) => e.stopPropagation()} role="dialog" aria-label={`기타 강좌: ${tutorial.title}`}>
            <TutorialHead>
              <span>{tutorial.title}</span>
              <button type="button" aria-label="닫기" onClick={() => setShowTutorial(false)}>✕</button>
            </TutorialHead>
            <TutorialFrame>
              <iframe
                src={`https://www.youtube.com/embed/${tutorial.id}?autoplay=1&playsinline=1&rel=0`}
                title={tutorial.title}
                allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                allowFullScreen
              />
            </TutorialFrame>
            <TutorialFoot>
              <span>{tutorial.channel}</span>
              <a href={`https://www.youtube.com/watch?v=${tutorial.id}`} target="_blank" rel="noopener noreferrer">유튜브에서 보기</a>
            </TutorialFoot>
          </TutorialBox>
        </TutorialDim>
      )}
      <ControllerWrapper>
        <NoteWrapper ref={noteScrollContainerRef} id="note-wrap">
          <NoteContainer id="note-container"
            ref={noteContainerRef}
            className={print ? "print" : ""}
            print={print}
            roomy={beta}
          >
            {room.code && !personal && (
              <EnsembleBar
                isHost={room.isHost}
                hostName={room.hostName}
                closed={room.closed}
                link={roomLink(Number(info?.id) || 0, room.code)}
                onClose={room.close}
                onLeave={room.leave}
              />
            )}
            <NoteHeaderWrapper>
              <NoteHeader>
                {/* 왼쪽 위 전체화면·화면 회전·공유 아이콘은 사장님 요청으로 뺌 (2026-10-02) */}
                {beta && <ToolBar>
                  {!isEn() && <Segmented role="group" aria-label="악보 보기 방식">
                    <SegButton type="button" active={viewMode === "bars"} onClick={() => setViewMode("bars")}>마디</SegButton>
                    <SegButton type="button" active={viewMode === "lyrics"} onClick={() => setViewMode("lyrics")}>가사</SegButton>
                  </Segmented>}
                  <ToolSpacer />
                  <EasyButton type="button" active={!!capo || simpleChord} onClick={() => { track("score:capo_open"); setIsCapoOpen(true); }}>
                    {capo ? `카포 ${capo.capo}${simpleChord ? " · 단순" : ""}` : simpleChord ? "단순 코드" : "쉬운 코드"}
                  </EasyButton>
                  {video && (
                    <VideoButton type="button" active={showVideo} onClick={() => {
                      track("score:video");
                      setIsPlaying(false);
                      setShowVideo(!showVideo);
                    }}>
                      <svg viewBox="0 0 24 24"><path d="M3 6h13v12H3zM16 10l5-3v10l-5-3" /></svg>
                      원곡
                    </VideoButton>
                  )}
                  {tutorial && (
                    <TutorialLink
                      href={`https://www.youtube.com/watch?v=${tutorial.id}`}
                      onClick={(e) => {
                        e.preventDefault();
                        track("score:tutorial");
                        setIsPlaying(false);
                        setShowTutorial(true);
                      }}
                      aria-label={`유튜브 기타 강좌: ${tutorial.title}`}
                    >
                      <svg viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                      강좌
                    </TutorialLink>
                  )}
                  {!personal && <ToolIconButton type="button" aria-label="악보 수정 요청" onClick={() => setIsReportModalOpen(true)}>
                    <svg viewBox="0 0 24 24"><path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" /></svg>
                  </ToolIconButton>}
                  {!personal && !room.code && (
                    <ToolIconButton type="button" aria-label="합주방 열기" onClick={async () => {
                      track("ensemble:open");
                      try {
                        await room.open();
                      } catch (e) {
                        alert((e as Error).message);
                      }
                    }}>
                      <svg viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM22 21v-2a4 4 0 0 0-3-3.9M16 3.1a4 4 0 0 1 0 7.8" /></svg>
                    </ToolIconButton>
                  )}
                  <ToolIconButton type="button" aria-label="연주 모드" onClick={enterPlayMode}>
                    <svg viewBox="0 0 24 24"><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>
                  </ToolIconButton>
                </ToolBar>}
{!beta && !personal && (<>
                <ReportButton>
                  <ReportIconWrap>
                    <ReportIcon />
                  </ReportIconWrap>
                </ReportButton>
                <ReportButton onClick={() => setIsReportModalOpen(true)}>
                  <ReportIconWrap>
                    <ReportIcon />
                  </ReportIconWrap>
                </ReportButton>
                </>)}
                {isFreeSong && <FreeBadge>이번 주 무료 곡 · 끝까지 볼 수 있어요</FreeBadge>}
                <NoteTitle>{info?.title}</NoteTitle>
                <NoteInfo>
                  <NoteInfoItem>{beta ? "원래 키" : "Original key"}: {info?.original_key}</NoteInfoItem>
                  <NoteInfoItem>{info?.singer}</NoteInfoItem>
                </NoteInfo>
                {beta && origShift !== 0 && (
                  <OrigKey type="button" active={atOrigKey} onClick={atOrigKey ? undefined : toOrigKey}>
                    {atOrigKey
                      ? "원곡과 같은 키로 보고 있어요"
                      : `원곡은 이 악보보다 반음 ${Math.abs(origShift)}개 ${origShift > 0 ? "높아요" : "낮아요"}`}
                    {!atOrigKey && <b>원곡 키로</b>}
                  </OrigKey>
                )}
                {info?.notice ? <NoteNotice>{info.notice}</NoteNotice> : null}
                <BeatContainer>{info?.beat.replace("/", "/")}</BeatContainer>
                <Modal
                  open={isReportModalOpen}
                  onClose={() => setIsReportModalOpen(false)}
                  firstButtonLabel="보내기"
                  onClickFirstButton={handleReport}
                  secondButtonLabel="취소"
                  onClickSecondButton={() => setIsReportModalOpen(false)}
                  title="악보 수정 요청"
                  content={
                    <>
                      <TextArea
                        value={reportValue}
                        onChange={(e) => setReportValue(e.target.value)}
                        placeholder="어디가 틀렸는지 알려 주세요. 예: 1절 '사랑해' 부분 코드가 C 가 아니라 Am 같아요"
                      />
                    </>
                  }
                />
              </NoteHeader>
            </NoteHeaderWrapper>
            {beta && practiceOn && (
              <PracticeCheck
                musicId={personal ? 0 : Number(info?.id) || 0}
                progress={currentProgress}
                isPlaying={isPlaying || videoPlaying}
                chord={practiceChord}
                bar={practiceBar}
                songChords={songChords}
                error={practiceErr}
                onClose={togglePractice}
              />
            )}
            {beta && showVideo && video && (
              <VideoSync
                musicId={info?.id || 0}
                youtubeId={video.id}
                offset={video.offset}
                barSec={Number(info?.sec) || (totalDurationSec && noteBarList.length ? totalDurationSec / noteBarList.length : 2)}
                barScales={noteBarList.map((b) => b.scale || 1)}
                onProgress={(p) => setCurrentProgress((prev: number) => (prev === p ? prev : p))}
                onPlayingChange={setVideoPlaying}
                onClose={() => setShowVideo(false)}
              />
            )}
            <MusicsWrap>
              <MusicsWaterMarkWrap>
                Chordscore.com
              </MusicsWaterMarkWrap>
              {/* Render notes here */}
              {viewMode === "lyrics" ? (
                <LyricsView
                  bars={noteBarList}
                  barsPerLine={screenWidth >= 900 ? 4 : 2}
                  progress={currentProgress}
                  stepMs={interval}
                  beatsPerBar={Number((info?.beat || "4/4").split("/")[0]) === 3 ? 3 : 4}
                  isPlaying={isPlaying || videoPlaying}
                  allChordCode={allChordCode}
                  stopPlay={() => setIsPlaying(false)}
                  onClickBar={(threshold) => onClickNoteBar(threshold)}
                />
              ) : noteBarList.map(({ lyrics, chords, meter, scale }, noteBarIndex) => {
                const isEndOfLine = (noteBarIndex + 1) % notesPerLine === 0;
                const isEndOfNote =
                  !isEndOfLine && noteBarIndex === noteBarList.length - 1;

                return (
                  <NoteBar
                    allChordCode={allChordCode}
                    key={`noteBar-${noteBarIndex}`}
                    chords={chords}
                    lyrics={lyrics}
                    meter={meter}
                    speedInSeconds={speedInSeconds * (scale || 1)}
                    progress={beta && isPlaying && !videoPlaying ? currentProgress + 25 : currentProgress}
                    threshold={noteBarIndex * 100}
                    isEndOfLine={isEndOfLine}
                    isEndOfNote={isEndOfNote}
                    hideProgress={isVisibleSpeed}
                    onClickNoteBar={onClickNoteBar}
                    stopPlay={() => setIsPlaying(false)}
                    isTransitionDisabled={isTransitionDisabled}
                    lyricsWidth={lyricsWidth}
                  />
                );
              })}
            </MusicsWrap>
            {previewData && previewData.total > previewData.rows && (
              <PreviewWall>
                <div className="fade" aria-hidden="true">
                  {[0, 1, 2].map((i) => (
                    <i key={i} />
                  ))}
                </div>
                <strong>여기까지 미리 보기예요</strong>
                <span>
                  {noPurchaseUI()
                    ? `남은 ${(previewData.total - previewData.rows) * 4}마디를 포함해 모든 악보는 이용권이 있는 계정으로 로그인하면 볼 수 있어요.`
                    : `남은 ${(previewData.total - previewData.rows) * 4}마디를 포함해 모든 악보를 끝까지 보려면 구독해 주세요.`}
                </span>
                {!noPurchaseUI() && (
                  <button type="button" onClick={() => { track("preview:cta"); history.push("/pricing"); }}>
                    월 3,300원으로 끝까지 보기
                  </button>
                )}
                <button type="button" className="ghost" onClick={() => history.push("/")}>
                  이번 주 무료 곡 보러 가기
                </button>
              </PreviewWall>
            )}
          </NoteContainer>
        </NoteWrapper>
        {beta ? (
          <Controller
            personal={personal}
            isControllerHidden={isControllerHidden}
            onClickToggleController={onClickToggleController}
            isPlaying={isPlaying}
            onClickPlayButton={onClickPlayButton}
            hideProgress={isVisibleSpeed}
            info={info}
            enableMetronome={enableMetronome}
            onClickMetronome={() => { if (!enableMetronome) { track("score:metronome"); prepareMetronome(); } setEnableMetronome(!enableMetronome); }}
            songBpm={songBpm}
            practiceOn={practiceOn}
            onClickPractice={togglePractice}
            onClickReset={onClickReset}
            progressRatio={noteBarList.length ? currentProgress / (noteBarList.length * 100) : 0}
            accStyle={accStyle}
            onClickAccompany={cycleAcc}
            durationSec={totalDurationSec / speed}
          />
        ) : (
          <ControllerLegacy
            personal={personal}
            isControllerHidden={isControllerHidden}
            onClickToggleController={onClickToggleController}
            isPlaying={isPlaying}
            onClickPlayButton={onClickPlayButton}
            hideProgress={isVisibleSpeed}
            info={info}
            enableMetronome={enableMetronome}
            onClickMetronome={() => setEnableMetronome(!enableMetronome)}
            onClickReset={onClickReset}
          />
        )}
        {beta && isFullScreen && (
          <PlayModeExit type="button" onClick={exitPlayMode}>
            <svg viewBox="0 0 24 24"><path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" /></svg>
            연주 모드 나가기
          </PlayModeExit>
        )}
        {beta && <CapoSheet open={isCapoOpen} chords={noteBarList.flatMap((b) => b.chords).map((c) => (simpleChord && c ? simplifyChord(c) : c))} onClose={() => setIsCapoOpen(false)} />}
        {isCounterRendered && (
          <CountOverlay step={countStep} count={countValue} />
        )}
      </ControllerWrapper>
      {!beta && (
        <BigReportButton onClick={() => setIsReportModalOpen(true)}>
          <BigReportIconWrap>
            <ReportIcon />
          </BigReportIconWrap>
        </BigReportButton>
      )}
      <Modal
        open={isCopyModelOpen}
        onClose={() => setIsCopyModelOpen(false)}
        firstButtonLabel="확인"
        onClickFirstButton={() => setIsCopyModelOpen(false)}
        title="링크 복사 되었습니다"
        content=""
      />
    </>
  );
};

export default NotePlayer;

const ControllerWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const NoteWrapper = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 7px;
  background-color: #f6f6f6;
  padding: 39px 62px;
  overflow: auto;
  overflow-x: hidden;
  padding-bottom: 0;
  scroll-behavior: smooth;
  -ms-overflow-style: none;
  /* 악보 끝까지 내려도 바깥 화면까지 같이 끌려가지 않게 (2026-10-12 아이패드 '화면 전체가 밀려 안 넘어가요' 신고) */
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;

  ::-webkit-scrollbar {
    display: none;
  }

  html[color-mode="dark"] & {
    background-color: #1a1e27;
  }

  @media screen and (max-width: 1200px) {
    padding: 0;
    border-radius: 0;
  }
`;

const NoteContainer = styled.div<{ print: boolean; roomy?: boolean }>`
  background-color: white;
  padding: 50px 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 104px;

  html[color-mode="dark"] & {
    background-color: #101111;
  }

  ${({ print }) =>
    print &&
    css`
      background-color: white !important;
    `}

  @media screen and (max-width: 1200px) {
    padding: 40px 50px;
    margin-bottom: 0px;
    padding-bottom: ${({ roomy }) => (roomy ? 200 : 150)}px;
  }

  @media screen and (max-width: 768px) {
    padding: 25px 14px;
    padding-bottom: ${({ roomy }) => (roomy ? 190 : 95)}px;
  }
`;

const NoteHeaderWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const NoteHeader = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: space-between;
  justify-content: center;
  margin: 0 auto 108px auto;

  @media screen and (max-width: 1200px) {
    margin-bottom: 98px;
  }

  @media screen and (max-width: 768px) {
    margin-bottom: 28px;
  }
`;

const NoteTitle = styled.h1`
  font-size: 30px; /* 38 → 30 (10/10 a-qa-barfont: 제목만 크고 코드·가사가 작았음) */
  font-weight: 900;
  color: #1e1e1e;
  margin: 0 auto 18px auto;

  html[color-mode="dark"] & {
    color: #ffffff;
  }

  .print & {
    color: #1e1e1e !important;
  }

  @media screen and (max-width: 1200px) {
    font-size: 28px;
  }

  @media screen and (max-width: 768px) {
    font-size: 12px;
  }
`;

const NoteInfo = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  
  @media screen and (max-width: 768px) {
    margin-bottom: 20px;
  }
`;

// 악보 상단 안내 문구 (예: AI 채보 악보 안내)
const NoteNotice = styled.p`
  width: 100%;
  margin: 12px 0 0 0;
  padding: 8px 12px;
  border-radius: 8px;
  background: #f3f4f6;
  color: #555;
  font-size: 15px;
  line-height: 1.5;
  text-align: center;
  word-break: keep-all;

  html[color-mode="dark"] & {
    background: #2a2a2a;
    color: #bbb;
  }

  @media screen and (max-width: 768px) {
    margin: 0 0 12px 0;
    font-size: 11px;
    padding: 6px 10px;
  }
`;

// 맛보기: 앞부분 뒤에 흐리게 가린 줄 + 구독 안내
const PreviewWall = styled.div`
  position: relative;
  margin: 8px 0 24px;
  padding: 0 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  color: #f3f4f6;
  & > .fade {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 22px;
    margin-bottom: 6px;
    filter: blur(3px);
    opacity: 0.5;
  }
  & > .fade > i {
    display: block;
    height: 12px;
    border-radius: 6px;
    background: linear-gradient(90deg, #2a2e38 0 22%, transparent 22% 30%, #2a2e38 30% 61%, transparent 61% 68%, #2a2e38 68% 90%, transparent 90%);
  }
  & > strong {
    font-size: 18px;
    font-weight: 800;
  }
  & > span {
    font-size: 14px;
    color: #a3a9b5;
    word-break: keep-all;
  }
  & > button {
    width: 100%;
    max-width: 360px;
    height: 50px;
    border: 0;
    border-radius: 14px;
    background: #ffb547;
    color: #0d0e12;
    font-size: 16px;
    font-weight: 800;
    cursor: pointer;
  }
  & > button.ghost {
    height: 44px;
    background: transparent;
    border: 1px solid #3a3f4b;
    color: #c7cbd3;
    font-size: 14px;
    font-weight: 600;
  }
`;

const FreeBadge = styled.span`
  align-self: center;
  margin-bottom: 6px;
  padding: 4px 10px;
  border-radius: 10px;
  background: #14231c;
  border: 1px solid #2f5d46;
  color: #3ddc97;
  font-size: 12px;
  font-weight: 700;
`;

// 원곡 키 안내 + '원곡 키로' 버튼 (원곡 비교로 키가 다르다고 확실한 곡만)
const OrigKey = styled.button<{ active: boolean }>`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 2px 0 8px;
  padding: 6px 6px 6px 12px;
  border-radius: 10px;
  border: 1px solid ${({ active }) => (active ? "#2f5d46" : "#2a3550")};
  background: ${({ active }) => (active ? "#14231c" : "#151a28")};
  color: ${({ active }) => (active ? "#3ddc97" : "#c7cbd3")};
  font-size: 13px;
  cursor: ${({ active }) => (active ? "default" : "pointer")};
  & > b {
    padding: 4px 10px;
    border-radius: 8px;
    background: #5b8cff;
    color: #fff;
    font-size: 13px;
    font-weight: 700;
  }
`;

const NoteInfoItem = styled.span`
  display: flex;
  font-size: 24px;

  @media screen and (max-width: 1200px) {
    font-size: 18px;
  }

  @media screen and (max-width: 768px) {
    font-size: 10px;
  }
`;

const MusicsWrap = styled.div`
  position: relative;
  display: flex;
  flex-wrap: wrap;
  width: 100%;
`;

const MusicsWaterMarkWrap = styled.div`
  position: fixed;
  /* 화면을 비스듬히 가로지르는 워터마크가 손가락 스크롤을 막지 않게 (가로 모드에서 특히 넓게 덮음) */
  pointer-events: none;
  font-size: 100px;
  opacity: 0.1;
  width: 100%;
  left: 0;
  top: 50%;
  transform: rotate(325deg);
  text-align: center;

  @media screen and (max-width: 768px) {
    font-size: 48px;
  }
`

const ReportButton = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;
  padding: 6px 8px;
  border-radius: 16.5px;
  cursor: pointer;

  &:first-of-type {
    opacity: 0;
    cursor: default;
  }

  @media (min-width: 950px) {
    display: none;
  }
`;

const ReportIconWrap = styled.div`
  width: 14px;
  position: absolute;
  top: 0px;
`;

const FullScreenIconWrap = styled.div`
  width: 30px;
  height: 30px;
  position: absolute;
  top: 0px;
  background-image: url("${FullScreenIcon}");
  background-size: cover;

  @media (min-width: 950px) {
    display: none;
  }
`;

const RotateIconWrap = styled.div`
  width: 30px;
  height: 30px;
  position absolute;
  top: 0px;
  left: 50px;
  background-image: url("${RotateIcon}");
  background-size: cover;

  @media (min-width: 950px) {
    display: none;
  }
`;

const DownloadIconWrap = styled.div`
  width: 32px;
  height: 30px;
  position: absolute;
  top: 0;
  right: 0;
  cursor: pointer;

  @media (max-width: 950px) {
    left: 150px;
  }
`;

const ShareIconWrap = styled.div`
  width: 32px;
  height: 30px;
  position: absolute;
  top: 0px;
  left: 100px;
  background-image: url("${ShareIcon}");
  background-size: cover;
  cursor: pointer;

  @media (min-width: 950px) {
    left: 0px;
  }
`;

const ExitScreenIconWrap = styled.div`
  z-index: 10;
  width: 30px;
  height: 30px;
  position: fixed;
  top: 60px;
  left: 7%;
  background-image: url("${ExitFullScreenIcon}");
  background-size: cover;

  @media (min-width: 950px) {
    display: none;
  }
`;

const BigReportButton = styled.div`
  display: flex;
  position: absolute;
  bottom: 34px;
  right: 34px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  width: 60px;
  height: 60px;
  border-radius: 14px;
  background-color: #0a84ff;
  box-shadow: 0 2px 7px 0 rgba(139, 139, 139, 0.78);

  &:first-of-type {
    opacity: 0;
    cursor: default;
  }

  @media (max-width: 950px) {
    display: none;
  }
`;

const BigReportIconWrap = styled.div`
  width: 20px;
  & > svg {
    width: 20px;
    height: 23px;
  }
`;

const BeatContainer = styled.div`
  height: 35px;
  border-radius: 6px;
  background-color: #202125;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.9;
  text-align: center;
  color: rgba(255, 255, 255, 0.6);
  padding: 4px 9px;
  position: absolute;
  bottom: 16px;
  left: 0;

  @media screen and (max-width: 768px) {
    height: 21px;
    border-radius: 4px;
    background-color: #202125;
    padding: 0 9px;
    font-size: 10px;
    line-height: 2;
  }
`;

// ── 상단 도구줄 (마디/가사 전환 · 쉬운 코드 · 연주 모드) ──
const ToolBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap; /* 버튼이 많으면 두 줄로 */
  row-gap: 8px;
  gap: 6px;
  width: 100%;
  margin-bottom: 18px;
  /* 오른쪽 위 '수정 요청'(사이렌) 아이콘 자리를 비워 둔다 */
  box-sizing: border-box;

  @media (min-width: 950px) {
    padding-right: 0;
  }
`;

const ToolSpacer = styled.div`
  flex: 1;
`;

const Segmented = styled.div`
  display: flex;
  padding: 3px;
  border-radius: 10px;
  background: #16181e;
  border: 1px solid #2a2e38;
`;

const SegButton = styled.button<{ active: boolean }>`
  height: 32px;
  padding: 0 12px;
  white-space: nowrap;
  border: 0;
  border-radius: 8px;
  background: ${({ active }) => (active ? "#2b3550" : "transparent")};
  color: ${({ active }) => (active ? "#f3f4f6" : "#a3a9b5")};
  font-size: 14px;
  font-weight: ${({ active }) => (active ? 600 : 400)};
  cursor: pointer;
  outline: 0;
`;

const EasyButton = styled.button<{ active: boolean }>`
  height: 38px;
  padding: 0 10px;
  border-radius: 10px;
  border: 1px solid ${({ active }) => (active ? "#ffb547" : "#3a3220")};
  background: ${({ active }) => (active ? "#ffb547" : "#1a1710")};
  color: ${({ active }) => (active ? "#0d0e12" : "#ffb547")};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  outline: 0;
  white-space: nowrap;
`;

const ToolIconButton = styled.button`
  width: 36px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid #2a2e38;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  outline: 0;

  & > svg {
    width: 18px;
    height: 18px;
    fill: none;
    stroke: #f3f4f6;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

const PlayModeExit = styled.button`
  position: fixed;
  top: calc(10px + env(safe-area-inset-top));
  right: 12px;
  z-index: 1000;
  height: 34px;
  padding: 0 12px;
  border-radius: 17px;
  border: 1px solid #2a2e38;
  background: rgba(22, 24, 30, 0.9);
  color: #a3a9b5;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  outline: 0;

  & > svg {
    width: 14px;
    height: 14px;
    fill: none;
    stroke: currentColor;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
`;

// 유튜브 기타 강좌 버튼
const TutorialLink = styled.a`
  height: 38px;
  padding: 0 10px;
  border-radius: 10px;
  border: 1px solid #3a2020;
  background: #1f1414;
  color: #ff8a8a;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  text-decoration: none;
  white-space: nowrap;
  & > svg {
    width: 14px;
    height: 14px;
    fill: #ff5f5f;
  }
`;

// 강좌 영상 창 (악보 위에 띄움)
const TutorialDim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`;
const TutorialBox = styled.div`
  width: 100%;
  max-width: 720px;
  background: #16181e;
  border-radius: 14px;
  overflow: hidden;
`;
const TutorialHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  color: #f3f4f6;
  font-size: 14px;
  font-weight: 600;
  & > span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  & > button {
    flex: none;
    width: 36px;
    height: 36px;
    border: 0;
    border-radius: 10px;
    background: #262a33;
    color: #f3f4f6;
    font-size: 16px;
    cursor: pointer;
  }
`;
const TutorialFrame = styled.div`
  position: relative;
  padding-top: 56.25%;
  background: #000;
  & > iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;
const TutorialFoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  font-size: 13px;
  color: #9aa0ab;
  & > a {
    color: #ff8a8a;
    text-decoration: none;
    white-space: nowrap;
  }
`;

// 원곡 영상 버튼
const VideoButton = styled.button<{ active: boolean }>`
  height: 38px;
  padding: 0 8px;
  border-radius: 10px;
  border: 1px solid ${({ active }) => (active ? "#5b8cff" : "#2a2e38")};
  background: ${({ active }) => (active ? "#2b3550" : "transparent")};
  color: #f3f4f6;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  white-space: nowrap;
  & > svg {
    width: 16px;
    height: 16px;
    fill: none;
    stroke: #f3f4f6;
    stroke-width: 1.8;
    stroke-linejoin: round;
  }
`;
